import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { STATUSES, PRIORITIES, ISSUE_TYPES } from "@/lib/meta";
import { Plus, Trash2, Zap, PlayCircle } from "lucide-react";
import { toast } from "sonner";

const EVENTS = [
  { id: "issue_created", label: "Issue is created" },
  { id: "issue_updated", label: "Issue is updated" },
  { id: "status_changed", label: "Status changes" },
  { id: "issue_assigned", label: "Assignee changes" },
];
const CONDITION_FIELDS = [
  { id: "priority", label: "Priority", options: PRIORITIES },
  { id: "issue_type", label: "Type", options: ISSUE_TYPES },
  { id: "status", label: "Status", options: STATUSES },
];

export default function Automations() {
  const { projects, users } = useApp();
  const [rules, setRules] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => setRules((await api.get("/automations")).data);
  useEffect(() => { load(); }, []);

  const save = async (r) => {
    if (r.id) await api.patch(`/automations/${r.id}`, r);
    else await api.post("/automations", r);
    toast.success(r.id ? "Rule updated" : "Rule created");
    setOpen(false); setEditing(null); load();
  };
  const del = async (id) => { if (!window.confirm("Delete rule?")) return; await api.delete(`/automations/${id}`); load(); };
  const toggle = async (r) => { await api.patch(`/automations/${r.id}`, { enabled: !r.enabled }); load(); };

  return (
    <div className="px-6 py-6 max-w-[1200px]">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">No-code</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Automations</h1>
          <p className="text-sm text-muted-foreground mt-1">When something happens, do something. Automate assignments, transitions, labels and comments.</p>
        </div>
        <Button onClick={()=>{ setEditing(newRule(projects[0]?.id)); setOpen(true); }} data-testid="new-automation-btn" className="gap-1.5"><Plus className="w-4 h-4" /> New rule</Button>
      </div>
      {rules.length === 0 ? (
        <Card className="p-16 text-center">
          <Zap className="w-8 h-8 mx-auto text-primary mb-3" />
          <div className="font-display text-lg font-semibold">No automation rules yet</div>
          <div className="text-sm text-muted-foreground mt-1 mb-4">Build a rule to save your team hours per week.</div>
          <Button onClick={()=>{ setEditing(newRule(projects[0]?.id)); setOpen(true); }}>Create your first rule</Button>
        </Card>
      ) : (
        <div className="space-y-2">
          {rules.map(r => {
            const proj = projects.find(p => p.id === r.project_id);
            return (
              <Card key={r.id} className="p-4 flex items-start gap-4" data-testid={`automation-${r.id}`}>
                <Switch checked={r.enabled} onCheckedChange={()=>toggle(r)} data-testid={`toggle-${r.id}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-medium">{r.name}</div>
                    <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground bg-secondary px-1.5 py-0.5 rounded">{proj?.key}</span>
                    <span className="text-[10px] text-muted-foreground">· ran {r.run_count} times</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    <span className="font-mono text-foreground">WHEN</span> {EVENTS.find(e=>e.id===r.trigger?.event)?.label || r.trigger?.event}
                    {r.conditions?.length > 0 && <> <span className="font-mono text-foreground">IF</span> {r.conditions.map((c,i)=>`${c.field} ${c.op} ${c.value}`).join(" AND ")}</>}
                    <> <span className="font-mono text-foreground">THEN</span> {r.actions?.map(a => a.type).join(", ") || "no actions"}</>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={()=>{ setEditing({...r}); setOpen(true); }}>Edit</Button>
                <Button variant="ghost" size="icon" onClick={()=>del(r.id)} data-testid={`del-automation-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
              </Card>
            );
          })}
        </div>
      )}
      <RuleDialog open={open} setOpen={setOpen} rule={editing} setRule={setEditing} onSave={save} projects={projects} users={users} />
    </div>
  );
}

function newRule(projectId) {
  return { project_id: projectId || "", name: "", enabled: true, trigger: { event: "issue_created" }, conditions: [], actions: [] };
}

function RuleDialog({ open, setOpen, rule, setRule, onSave, projects, users }) {
  if (!rule) return null;
  const upd = (patch) => setRule({ ...rule, ...patch });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl" data-testid="rule-dialog">
        <DialogHeader><DialogTitle className="font-display">{rule.id ? "Edit" : "New"} automation rule</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={rule.name} onChange={(e)=>upd({name:e.target.value})} placeholder="e.g. Auto-assign critical bugs" data-testid="rule-name" /></div>
            <div><Label>Project</Label>
              <Select value={rule.project_id} onValueChange={(v)=>upd({project_id:v})}>
                <SelectTrigger data-testid="rule-project"><SelectValue /></SelectTrigger>
                <SelectContent>{projects.map(p=><SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <Section title="WHEN" testid="rule-when">
            <Select value={rule.trigger?.event} onValueChange={(v)=>upd({trigger:{event:v}})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{EVENTS.map(e=><SelectItem key={e.id} value={e.id}>{e.label}</SelectItem>)}</SelectContent>
            </Select>
          </Section>
          <Section title="IF" onAdd={()=>upd({conditions:[...rule.conditions,{field:"priority",op:"eq",value:"highest"}]})} testid="rule-if">
            {rule.conditions.length === 0 && <div className="text-xs text-muted-foreground">No conditions (rule always runs)</div>}
            {rule.conditions.map((c,i)=>(
              <div key={i} className="grid grid-cols-[1fr_100px_1fr_auto] gap-2 items-center">
                <Select value={c.field} onValueChange={(v)=>{ const cs=[...rule.conditions]; cs[i]={...c,field:v,value:""}; upd({conditions:cs}); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CONDITION_FIELDS.map(f=><SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={c.op} onValueChange={(v)=>{ const cs=[...rule.conditions]; cs[i]={...c,op:v}; upd({conditions:cs}); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="eq">equals</SelectItem><SelectItem value="neq">not equals</SelectItem></SelectContent>
                </Select>
                <Select value={c.value} onValueChange={(v)=>{ const cs=[...rule.conditions]; cs[i]={...c,value:v}; upd({conditions:cs}); }}>
                  <SelectTrigger><SelectValue placeholder="value" /></SelectTrigger>
                  <SelectContent>
                    {(CONDITION_FIELDS.find(f=>f.id===c.field)?.options || []).map(o => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="icon" onClick={()=>upd({conditions:rule.conditions.filter((_,j)=>j!==i)})}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </Section>
          <Section title="THEN" onAdd={()=>upd({actions:[...rule.actions,{type:"add_comment",body:""}]})} testid="rule-then">
            {rule.actions.length === 0 && <div className="text-xs text-muted-foreground">No actions yet — add one</div>}
            {rule.actions.map((a,i)=>(
              <div key={i} className="grid grid-cols-[140px_1fr_auto] gap-2 items-center">
                <Select value={a.type} onValueChange={(v)=>{ const as=[...rule.actions]; as[i]={type:v}; upd({actions:as}); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="assign">Assign</SelectItem>
                    <SelectItem value="set_status">Set status</SelectItem>
                    <SelectItem value="set_priority">Set priority</SelectItem>
                    <SelectItem value="add_label">Add label</SelectItem>
                    <SelectItem value="add_comment">Add comment</SelectItem>
                  </SelectContent>
                </Select>
                {a.type === "assign" && (
                  <Select value={a.user_id||""} onValueChange={(v)=>{ const as=[...rule.actions]; as[i]={...a,user_id:v}; upd({actions:as}); }}>
                    <SelectTrigger><SelectValue placeholder="user" /></SelectTrigger>
                    <SelectContent>{users.map(u=><SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                {a.type === "set_status" && (
                  <Select value={a.status||""} onValueChange={(v)=>{ const as=[...rule.actions]; as[i]={...a,status:v}; upd({actions:as}); }}>
                    <SelectTrigger><SelectValue placeholder="status" /></SelectTrigger>
                    <SelectContent>{STATUSES.map(s=><SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                {a.type === "set_priority" && (
                  <Select value={a.priority||""} onValueChange={(v)=>{ const as=[...rule.actions]; as[i]={...a,priority:v}; upd({actions:as}); }}>
                    <SelectTrigger><SelectValue placeholder="priority" /></SelectTrigger>
                    <SelectContent>{PRIORITIES.map(p=><SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                {a.type === "add_label" && <Input value={a.label||""} placeholder="label name" onChange={(e)=>{ const as=[...rule.actions]; as[i]={...a,label:e.target.value}; upd({actions:as}); }} />}
                {a.type === "add_comment" && <Input value={a.body||""} placeholder="comment text" onChange={(e)=>{ const as=[...rule.actions]; as[i]={...a,body:e.target.value}; upd({actions:as}); }} />}
                <Button variant="ghost" size="icon" onClick={()=>upd({actions:rule.actions.filter((_,j)=>j!==i)})}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </Section>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
          <Button onClick={()=>onSave(rule)} data-testid="rule-save">Save rule</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children, onAdd, testid }) {
  return (
    <div className="border border-border rounded-md p-3 space-y-2" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest font-mono font-medium text-primary">{title}</div>
        {onAdd && <Button size="sm" variant="ghost" onClick={onAdd} className="h-7 text-xs gap-1"><Plus className="w-3 h-3" /> Add</Button>}
      </div>
      {children}
    </div>
  );
}
